import { isTvTab } from "../lib/tvTab";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { authErrorMessage, useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import { levelInfo, levelTitle } from "../lib/xp";
import { cleanName, exportMyData, updateProfileFields } from "../lib/profile";
import { Avatar, AvatarPicker, Modal, SoundToggle, ThemeToggle, XPBar, copyText } from "./ui";
import { EVENTS, useEvent } from "../lib/season";
import LanguagePicker from "./LanguagePicker";
import { StreamerToggle } from "./Streamer";
import { useI18n, useT } from "../i18n";
import { withLang } from "../i18n/paths";
import { isAdmin } from "../lib/admin";
import Pic from "./pics";

// Download my data / Delete my account (GDPR rights), at the bottom of the profile.
function YourData({ onClose }) {
  const t = useT();
  const { user, deleteAccount } = useAuth();
  const { toast } = useToast();
  const [deleting, setDeleting] = useState(false);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const hasPassword = user?.providers?.includes("password");

  const download = async () => {
    try {
      const data = await exportMyData(user);
      const url = URL.createObjectURL(new Blob([JSON.stringify(data, null, 2)], { type: "application/json" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "partycade-my-data.json";
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      toast({ emoji: "warning", kind: "error", title: t("Couldn't download your data"), body: e.message });
    }
  };

  const remove = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      // False: a phone went to Google to confirm, and finishes when it comes back.
      if (!(await deleteAccount(hasPassword ? password : null))) return;
      onClose();
      toast({ emoji: "hand", title: t("Your account was deleted"), body: t("Thanks for playing. You can still play as a guest.") });
    } catch (err) {
      setError(authErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="your-data">
      <h3>{t("Your data")}</h3>
      <div className="your-data-actions">
        <button className="btn btn-ghost btn-sm" onClick={download}>
          <Pic name="download" /> {t("Download my data")}
        </button>
        <button className="btn btn-ghost btn-sm danger-text" onClick={() => setDeleting((d) => !d)}>
          {t("Delete my account")}
        </button>
      </div>
      {deleting && (
        <form className="delete-account" onSubmit={remove}>
          <p>
            {t(
              "This permanently deletes your login, name, photo, friends, friend requests and invites. Your XP and level can't be brought back."
            )}
          </p>
          {hasPassword ? (
            <label className="field">
              {t("Your password")}
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" />
            </label>
          ) : (
            <p className="muted small">{t("You'll confirm with Google in a popup.")}</p>
          )}
          <label className="field">
            {t("Type {word} to confirm", { word: DELETE_WORD })}
            <input value={confirm} onChange={(e) => setConfirm(e.target.value)} autoCapitalize="characters" />
          </label>
          {error && <p className="form-error">{error}</p>}
          <button className="btn btn-danger btn-block" disabled={busy || confirm.trim().toUpperCase() !== DELETE_WORD || (hasPassword && !password)}>
            {busy ? t("Deleting…") : t("Delete my account forever")}
          </button>
        </form>
      )}
    </div>
  );
}

// Typed to confirm deleting an account (the same in every language).
const DELETE_WORD = "DELETE";

export function ProfileModal({ open, onClose }) {
  const t = useT();
  const { profile } = useAuth();
  const { toast } = useToast();
  const [name, setName] = useState("");
  const [avatar, setAvatar] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && profile) {
      setName(profile.displayName);
      setAvatar(profile.avatar);
    }
  }, [open, profile]);

  if (!profile || !avatar) return null;
  const info = levelInfo(profile.xp);

  const save = async () => {
    const n = cleanName(name);
    if (n.length < 2) return toast({ emoji: "hand", title: t("Name needs at least 2 characters") });
    setSaving(true);
    try {
      await updateProfileFields(profile.uid, { displayName: n, avatar });
      toast({ emoji: "save", title: t("Profile saved") });
      onClose();
    } catch (e) {
      toast({ emoji: "warning", kind: "error", title: t("Couldn't save"), body: e.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title={t("Your profile")} className="profile-modal">
      <div className="profile-hero">
        <Avatar avatar={avatar} size={88} />
        <div>
          <h2>{cleanName(name) || profile.displayName}</h2>
          <p className="muted">
            {t("Level {level}", { level: info.level })} · {t(levelTitle(info.level))} · {t("{xp} XP total", { xp: profile.xp })}
          </p>
          <button
            className="friend-code"
            onClick={async () => {
              if (await copyText(profile.friendCode)) toast({ emoji: "copy", title: t("Friend code copied!") });
            }}
            title={t("Copy friend code")}
          >
            #{profile.friendCode} <Pic name="copy" />
          </button>
        </div>
      </div>
      <XPBar xp={profile.xp} />
      <p className="hint">{t("Earn XP just by hanging out: 10 XP/min in a game, 4 XP/min in a lobby. Winning adds a bonus!")}</p>
      <label className="field">
        {t("Display name")}
        <input value={name} onChange={(e) => setName(e.target.value)} maxLength={20} />
      </label>
      <div className="field">
        <span>{t("Avatar")}</span>
        <AvatarPicker value={avatar} onChange={setAvatar} />
      </div>
      <button className="btn btn-primary btn-block" onClick={save} disabled={saving}>
        {saving ? t("Saving…") : t("Save profile")}
      </button>
      <YourData onClose={onClose} />
    </Modal>
  );
}

export default function Header() {
  const { t, lang } = useI18n();
  const { user, profile, isGuest, guest, openAuth, logout } = useAuth();
  const [menu, setMenu] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const menuRef = useRef(null);
  const event = EVENTS[useEvent()];

  useEffect(() => {
    if (!menu) return;
    const close = (e) => menuRef.current && !menuRef.current.contains(e.target) && setMenu(false);
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [menu]);

  const level = profile ? levelInfo(profile.xp).level : null;

  return (
    <header className="site-header">
      <Link to={withLang(lang, "/")} className="logo">
        <span className="logo-cards" aria-hidden>
          <i><Pic name={event ? event.logo[0] : "cards"} /></i>
          <i><Pic name={event ? event.logo[1] : "palette"} /></i>
        </span>
        <span className="logo-text">
          Party<b>Cade</b>
        </span>
      </Link>

      <div className="header-right">
        <LanguagePicker />
        <StreamerToggle />
        <ThemeToggle />
        <SoundToggle />
        {profile ? (
          <div className="user-menu" ref={menuRef}>
            <button className="user-chip" onClick={() => setMenu((m) => !m)}>
              <Avatar avatar={profile.avatar} size={36} level={level} />
              <span className="user-chip-text">
                <strong>{profile.displayName}</strong>
                <XPBar xp={profile.xp} compact />
              </span>
            </button>
            {menu && (
              <div className="dropdown">
                <button
                  onClick={() => {
                    setProfileOpen(true);
                    setMenu(false);
                  }}
                >
                  {t("Edit profile")}
                </button>
                {isAdmin(user) && (
                  <Link to="/admin" className="dropdown-link" onClick={() => setMenu(false)}>
                    <Pic name="shield" /> Moderation
                  </Link>
                )}
                <button
                  onClick={() => {
                    logout();
                    setMenu(false);
                  }}
                >
                  {t("Sign out")}
                </button>
              </div>
            )}
          </div>
        ) : user && isGuest ? (
          <div className="guest-chip">
            <Avatar avatar={guest.avatar} size={32} />
            <span className="guest-chip-text">
              <strong>{guest.name || t("Guest")}</strong>
              {!isTvTab() && <small>{t("Guest · XP not saved")}</small>}
            </span>
            {!isTvTab() && (
              <button className="btn btn-sm btn-primary" onClick={() => openAuth("signup")}>
                <span className="narrow-only">{t("Save")}</span>
                <span className="wide-only">{t("Save progress")}</span>
              </button>
            )}
          </div>
        ) : (
          <>
            <button className="btn btn-ghost" onClick={() => openAuth("signin")}>
              {t("Sign in")}
            </button>
            <button className="btn btn-primary header-signup" onClick={() => openAuth("signup")}>
              {t("Sign up")}
            </button>
          </>
        )}
      </div>
      <ProfileModal open={profileOpen} onClose={() => setProfileOpen(false)} />
    </header>
  );
}
